import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Button, Card, message, Typography, Alert } from "antd";
import { EnvironmentOutlined, RightOutlined, ExportOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
// import { generateDummyBookingConfirmation } from "../../utils/dummyBookingData"; // superseded - see handleOpenScheduler
import { withPatientReferenceParams } from "../../services/zoomSchedulerLinks";
import { baseUrl } from "../../utils/envAccess";
import fertilityImage from "../../assets/images/auth/fertilityImage.svg";
import neutralDoctorAvatar from "../../assets/images/users/clinician_default_avatar.png";
import "../Register/Register.css";
import "../Register/SignUpPages/SignupPages.css";
import "./QuickSelfBooking.css";

const { Text, Title } = Typography;

function uniq(arr) {
  return Array.from(new Set(arr));
}

function normalizeLocation(value) {
  return String(value || "").trim().toLowerCase();
}

function toTitleCase(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export default function QuickSelfBooking() {
  const navigate = useNavigate();
  const [step, setStep] = useState("clinician");
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [openedScheduler, setOpenedScheduler] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).has("new")) {
    localStorage.removeItem("quickPatientDraft");
    localStorage.removeItem("quickBookingConfirmation");
    localStorage.removeItem("quickBookingProvider");
  }

  const draft = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("quickPatientDraft") || "null");
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!draft) navigate("/quick-start", { replace: true });
  }, [draft, navigate]);

  // The logged-out-quick-signup patient, for Zoom Scheduler prefill (see
  // withPatientReferenceParams) - same shape BookingModal.jsx builds from a
  // real logged-in user's auth state, sourced here from the quick-signup
  // draft instead since this flow never issues a login token.
  const patient = useMemo(() => {
    const quickResp = draft?.quickSignUpResponse || {};
    return {
      firstName: draft?.firstName,
      lastName: draft?.lastName,
      email: draft?.email,
      id:
        quickResp?.patientId ??
        quickResp?.PatientId ??
        quickResp?.id ??
        quickResp?.Id ??
        quickResp?.userId ??
        quickResp?.UserId,
    };
  }, [draft]);

  useEffect(() => {
    let cancelled = false;

    const fetchDoctors = async () => {
      setLoadingDoctors(true);
      try {
        const date = moment().add(7, "days").format("YYYY-MM-DD");
        const res = await axios.get(`${baseUrl}Patient/GetAllDoctorsAllTimeSlots`, {
          params: { date },
          headers: { accept: "text/plain" },
        });

        const raw = res?.data?.data ?? res?.data;
        const items = Array.isArray(raw) ? raw : raw?.items || raw?.doctors || raw?.results || [];
        const list = Array.isArray(items) ? items : [];

        const mapped = list
          .map((d) => {
            // Backend may return: { userId, doctorName, email, slots: [{ start, end }] }
            const doctorId = d?.doctorId ?? d?.DoctorId ?? d?.id ?? d?.Id ?? d?.userId ?? d?.UserId;
            const name =
              d?.name ?? d?.Name ?? d?.doctorName ?? d?.DoctorName ?? d?.doctorFullName ?? d?.doctor ?? d?.doctorName;
            if (!doctorId || !name) return null;

            const title =
              d?.roleName ?? d?.RoleName ?? d?.title ?? d?.Title ?? d?.role ?? d?.Role ?? d?.designation ?? "";


            const slotsRaw = d?.slots ?? d?.Slots ?? [];
            const slotLocations = uniq(
              (Array.isArray(slotsRaw) ? slotsRaw : [])
                .map((s) => toTitleCase(s?.location ?? s?.Location ?? ""))
                .filter(Boolean),
            );
            const location = slotLocations.join(", ");

            const photo =
              d?.picture ||
              d?.Picture ||
              d?.photo ||
              d?.Photo ||
              d?.profile ||
              d?.Profile ||
              d?.avatar ||
              d?.Avatar ||
              neutralDoctorAvatar;

            return {
              id: `doctor-${doctorId}`,
              doctorId: Number(doctorId),
              name: String(name),
              title: String(title || ""),
              bio: d?.bio ?? d?.Bio ?? "",
              location,
              locations: slotLocations,
              photo,
              // Quick Start is initial appointments only, so only the initial link is used.
              bookingLink: d?.initialBookingLink ?? d?.InitialBookingLink ?? "",
            };
          })
          .filter(Boolean)
          .filter((d) => !!d.bookingLink);

        if (!cancelled) setDoctors(mapped);
      } catch (e) {
        if (!cancelled) {
          setDoctors([]);
          message.error("Could not load doctors. Please try again.");
        }
      } finally {
        if (!cancelled) setLoadingDoctors(false);
      }
    };

    fetchDoctors();

    return () => {
      cancelled = true;
    };
  }, []);

  
  const patientLocation = useMemo(() => normalizeLocation(draft?.location), [draft]);

  const filteredClinicians = useMemo(() => {
    if (!patientLocation) return doctors;

    const matching = doctors.filter((c) =>
      (c.locations ?? []).some((l) => normalizeLocation(l) === patientLocation),
    );
    const noLocation = doctors.filter((c) => !(c.locations ?? []).length);

    return [...matching, ...noLocation];
  }, [doctors, patientLocation]);

  const handlePickClinician = (c) => {
    setSelectedClinician(c);
    setOpenedScheduler(false);
    setStep("schedule");
  };

  const handleBackToClinicians = () => {
    setStep("clinician");
    setSelectedClinician(null);
    setOpenedScheduler(false);
  };

  const schedulerUrl = useMemo(
    () => withPatientReferenceParams(selectedClinician?.bookingLink, patient),
    [selectedClinician, patient],
  );

  const handleOpenScheduler = () => {
    if (!schedulerUrl || !selectedClinician) return;
    window.open(schedulerUrl, "_blank", "noopener,noreferrer");
    setOpenedScheduler(true);

    
    localStorage.setItem(
      "quickBookingConfirmation",
      JSON.stringify({
        providerName: selectedClinician.name,
        providerRole: selectedClinician.title,
        date: null,
        startTime: null,
        endTime: null,
        meetingType: "Zoom",
        meetingLink: null,
        confirmationCode: null,
      }),
    );
    localStorage.setItem(
      "quickBookingProvider",
      JSON.stringify({
        id: selectedClinician.id,
        doctorId: selectedClinician.doctorId,
        name: selectedClinician.name,
        title: selectedClinician.title,
        location: selectedClinician.location,
        bookingLink: selectedClinician.bookingLink,
      }),
    );
  };

  const handleContinue = () => {
    navigate("/quick-confirmation");
  };

  const accentBtn = {
    height: "46px",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "16px",
    color: "#ffffff",
    backgroundColor: "#00ADEF",
    border: "1px solid #00ADEF",
  };

  return (
    <div className="">
      <div className="full-width-container">
        <div className="quick-book-viewport">
          <div className="quick-book-viewport-inner">
            <nav className="navbar mt-3">
                  <img
                    className="float-left"
                    src={fertilityImage}
                    alt="myfertility"
                    style={{ width: "150px" }}
                  />
                  <div className="d-flex">
                    <NavLink
                      to="/"
                      className="btn-user btn-block"
                      style={{
                        ...accentBtn,
                        minWidth: "126px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 20px",
                        lineHeight: 1,
                        textDecoration: "none",
                      }}
                    >
                      Sign In
                    </NavLink>
                  </div>
                </nav>

                <div className="card o-hidden border-0 my-3">
                  <div className="card-body p-4 p-md-5">
                    {step === "clinician" && (
                      <div className="quick-book-clinician-panel">
                        <div className="quick-book-clinician-panel-intro">
                          <Title level={4} style={{ color: "#000", fontWeight: 700, marginBottom: 12 }}>
                            Book your appointment
                          </Title>
                          <Text style={{ display: "block", color: "#595959", fontSize: 15, lineHeight: 1.6 }}>
                            You can book your appointment now.
                          </Text>
                          <Text style={{ display: "block", color: "#595959", fontSize: 15, lineHeight: 1.6, marginTop: 8 }}>
                            An account will be created with your email so you can securely access your care
                            information once setup is complete.
                          </Text>
                          <Text style={{ display: "block", color: "#262626", fontSize: 15, lineHeight: 1.6, marginTop: 16, fontWeight: 500 }}>
                            Choose who you would like to book with
                          </Text>
                        </div>

                        <div>
                          <div className="quick-book-section-header">
                            <p className="quick-book-clinician-label">CLINICIAN</p>
                          </div>

                          <div
                            style={{
                              borderRadius: 10,
                              overflow: "hidden",
                              background: "#fff",
                            }}
                          >
                            {loadingDoctors && (
                              <div style={{ padding: 16 }}>
                                <Text type="secondary">Loading doctors…</Text>
                              </div>
                            )}
                            {filteredClinicians.map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                className="quick-book-clinician-row"
                                onClick={() => handlePickClinician(c)}
                              >
                                <img
                                  className="quick-book-clinician-avatar"
                                  src={c.photo}
                                  alt=""
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = neutralDoctorAvatar;
                                  }}
                                />
                                <div className="quick-book-clinician-body">
                                  <h3 className="quick-book-clinician-name">
                                    {c.name}
                                    {c.title ? ` (${c.title})` : ""}
                                  </h3>
                                  {!!c.bio && <p className="quick-book-clinician-bio">{c.bio}</p>}
                                  {!!c.location && (
                                    <div className="quick-book-clinician-location">
                                      <EnvironmentOutlined
                                        className="quick-book-location-icon"
                                        aria-hidden
                                      />
                                      <span className="quick-book-location-text">
                                        {c.location}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <RightOutlined className="quick-book-chevron" />
                              </button>
                            ))}
                          </div>

                          {!loadingDoctors && filteredClinicians.length === 0 && (
                            <Text type="secondary" style={{ display: "block", textAlign: "center", marginTop: 24 }}>
                              No doctors available right now. Please try another date or check back later.
                            </Text>
                          )}

                          <div style={{ marginTop: 24, textAlign: "center" }}>
                            <Button type="link" onClick={() => navigate("/quick-start")} style={{ color: "#01ADF0" }}>
                              ← Back to your details
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {step === "schedule" && selectedClinician && (
                      <div style={{ maxWidth: 560, margin: "0 auto" }}>
                        <Button type="link" onClick={handleBackToClinicians} style={{ paddingLeft: 0, marginBottom: 16, color: "#01ADF0" }}>
                          ← Back to clinicians
                        </Button>
                        <Title level={4} style={{ marginBottom: 8 }}>
                          Book with {selectedClinician.name}
                        </Title>
                        <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
                          {selectedClinician.name} · {selectedClinician.location}
                        </Text>

                        <Card bordered>
                          {schedulerUrl ? (
                            <>
                              <Text style={{ display: "block", color: "#595959", marginBottom: 20 }}>
                                Pick a time on {selectedClinician.name}&apos;s scheduling page. Their live
                                availability lives there, and you&apos;ll get a confirmation email with the
                                meeting details once you&apos;re booked.
                              </Text>

                              {openedScheduler && (
                                <Alert
                                  type="info"
                                  showIcon
                                  style={{ marginBottom: 20 }}
                                  message="Finish booking in the new tab"
                                  description="Once you've picked a time and submitted it on Zoom's page, you're set - you'll get a confirmation email from Zoom. Come back here and continue for what's next."
                                />
                              )}

                              <Alert
                                type="warning"
                                showIcon
                                style={{ marginBottom: 20 }}
                                message="Complete at least 72 hours before your appointment:"
                                description={
                                  <>
                                    <ul style={{ margin: "4px 0 8px", paddingLeft: 18 }}>
                                      <li>Complete any required intake forms</li>
                                      <li>Ensure you have the required plan or appointment credits</li>
                                    </ul>
                                    If these steps aren&apos;t completed at least 72 hours before your
                                    appointment, it may be automatically cancelled so the time can be
                                    offered to another patient.
                                  </>
                                }
                              />

                              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
                                <Button onClick={handleBackToClinicians}>Cancel</Button>
                                {openedScheduler ? (
                                  <Button
                                    type="primary"
                                    style={{ background: "#01ADF0", borderColor: "#01ADF0" }}
                                    onClick={handleContinue}
                                  >
                                    Continue
                                  </Button>
                                ) : (
                                  <Button
                                    type="primary"
                                    icon={<ExportOutlined />}
                                    style={{ background: "#01ADF0", borderColor: "#01ADF0" }}
                                    onClick={handleOpenScheduler}
                                  >
                                    Schedule Appointment
                                  </Button>
                                )}
                              </div>
                            </>
                          ) : (
                            <>
                              <Text style={{ display: "block", color: "#595959", marginBottom: 20 }}>
                                {selectedClinician.name} doesn&apos;t have a scheduling link set up yet.
                                Please contact us and we&apos;ll get you booked.
                              </Text>
                              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                                <Button onClick={handleBackToClinicians}>Back</Button>
                              </div>
                            </>
                          )}
                        </Card>
                      </div>
                    )}
                  </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
